/**
 * Enter a food by hand.
 *
 * This is the escape hatch every other path needs. A barcode lookup misses,
 * OCR cannot read a creased label, the thing came out of somebody's kitchen and
 * has no packaging at all — without this the answer is "sorry", and the whole
 * app is only as good as its worst scan.
 *
 * It opens from a search that found nothing, prefilled with what was typed,
 * because that is the moment the need appears. Reaching it any other way would
 * mean knowing in advance that the search would fail.
 *
 * Per 100 g, like the rest of the system. That is a real demand to make of
 * someone holding a packet that says "per serving (45 g)", so the label says so
 * plainly rather than letting them enter a portion figure into a per-100 field
 * — which produces a food wrong by whatever the serving size was, silently, and
 * for as long as they keep logging it.
 */

import {
  figureChange,
  figureValue,
  NUTRIENTS,
  parseDecimal,
  seedFigure,
  sodiumMg,
  useCreateFood,
  type FigureSpec,
  type FoodItem,
} from "@calorio/core";
import { useState, type FormEvent, type RefObject } from "react";

import { OptionalDetail } from "./OptionalDetail";
import { useCalorio } from "../app/providers";

/** Figures a failed label read did manage, per 100. */
export interface PartialNutrition {
  energy?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  saturates?: number;
  fiber?: number;
  sugar?: number;
  /** Grams of salt, as an EU panel prints it. */
  salt?: number;
  /** Milligrams of sodium, as a US panel prints it. */
  sodium?: number;
}

/**
 * How each optional figure is shown, seeded and compared, by the column it
 * writes to. Salt is the only one that does anything: stored as sodium in
 * milligrams, typed in grams, because grams is what the packet says.
 */
const byKey: Record<string, FigureSpec> = Object.fromEntries(
  NUTRIENTS.map((spec) => [
    spec.per100Key,
    { decimals: spec.decimals, display: spec.display, store: spec.store },
  ]),
);

/**
 * What the label reader managed, in the units this form stores.
 *
 * A panel prints salt or sodium, never both, and the reader hands back
 * whichever it saw. Converting here — with the shared factor, not a second
 * copy of it — means the caller does not have to know which one arrived.
 */
function readerSodium(initial: PartialNutrition | undefined): number | undefined {
  if (initial?.sodium !== undefined) return initial.sodium;
  if (initial?.salt !== undefined) return sodiumMg(initial.salt);
  return undefined;
}

interface ManualFoodProps {
  /** What they searched for. Almost always the name they want. */
  initialName?: string;
  /** The brand, when a label read got that far. */
  initialBrand?: string;
  /**
   * What the reader got before it gave up. Pre-filling these is the difference
   * between "type this label out" and "fill in the one row I could not read".
   */
  initial?: PartialNutrition;
  /**
   * This form is finishing a label the reader could not.
   *
   * It changes what an empty field *means*. Reaching this form from a search
   * that found nothing, every box is blank because nothing has been read yet.
   * Reaching it from a half-read panel, a blank box is a specific question —
   * that row was on the packet and the reader missed it — so it is marked
   * "couldn't read", given the action border as the next thing to fill, and its
   * placeholder is an em dash rather than a `0` that would look like an answer.
   */
  salvage?: boolean;
  /** Lets a caller send focus here — see the "nothing to read" refusal, where
   *  the form is already open and the button is a pointer to it. */
  nameRef?: RefObject<HTMLInputElement | null>;
  /**
   * Drop the card, the heading and the Cancel — the surface around this already
   * has all three.
   *
   * Inside the add sheet the header names the meal and carries the only way
   * out, and a card inside a sheet is a box inside a box. The fields are the
   * same fields; only the chrome that would be said twice goes.
   */
  bare?: boolean;
  onCreated: (food: FoodItem) => void;
  onCancel: () => void;
}

export function ManualFood({
  initialName = "",
  initialBrand = "",
  initial,
  salvage = false,
  nameRef,
  bare = false,
  onCreated,
  onCancel,
}: ManualFoodProps) {
  const ctx = useCalorio();
  const create = useCreateFood(ctx);

  const [name, setName] = useState(initialName);
  const [brand, setBrand] = useState(initialBrand);
  const [unit, setUnit] = useState<"g" | "ml">("g");
  // A figure the reader missed stays blank rather than becoming 0 — a blank is
  // a question, a zero is an assertion, and the whole point of refusing the
  // panel was to avoid asserting something nobody read.
  const show = (value: number | undefined) => (value === undefined ? "" : String(value));
  const [kcal, setKcal] = useState(show(initial?.energy));
  const [protein, setProtein] = useState(show(initial?.protein));
  const [carbs, setCarbs] = useState(show(initial?.carbs));
  const [fat, setFat] = useState(show(initial?.fat));
  const [portion, setPortion] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * The optional four, keyed by the column they write to.
   *
   * Seeded through `seedFigure` so salt arrives in grams — it is stored as
   * sodium in milligrams, and grams is what the packet in front of somebody
   * says.
   */
  const [detail, setDetail] = useState<Record<string, string>>(() => ({
    saturated_fat_g_per_100: seedFigure(initial?.saturates, byKey.saturated_fat_g_per_100),
    fiber_g_per_100: seedFigure(initial?.fiber, byKey.fiber_g_per_100),
    sugar_g_per_100: seedFigure(initial?.sugar, byKey.sugar_g_per_100),
    sodium_mg_per_100: seedFigure(readerSodium(initial), byKey.sodium_mg_per_100),
  }));
  /* Open when the reader actually got one of them. A value that was read and
     then hidden behind a toggle is a value that gets lost — which is the one
     thing salvage mode exists to prevent. Otherwise shut: four empty boxes on
     the way to Save is what the toggle is for. */
  const detailRead = Object.values(detail).some((value) => value !== "");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);

    if (!name.trim()) return setProblem("Give it a name.");
    const energy = parseDecimal(kcal);
    if (!Number.isFinite(energy) || energy < 0) return setProblem("Calories per 100 must be a number.");

    // Macros default to zero rather than blocking: someone entering a drink
    // knows the calories and may genuinely not know the rest, and refusing the
    // whole food over a blank fat field helps nobody.
    const macros = { protein_g_per_100: parseDecimal(protein) || 0, carbs_g_per_100: parseDecimal(carbs) || 0, fat_g_per_100: parseDecimal(fat) || 0 };
    if (Object.values(macros).some((v) => !Number.isFinite(v) || v < 0)) {
      return setProblem("Macros must be numbers, or left blank.");
    }

    // Unlike the macros above, a blank here stays null. These are genuinely
    // optional columns, and `|| 0` would turn "I don't know" into "none" — on
    // a food that may end up shared. `figureChange` also refuses anything that
    // is not a number, rather than letting it through as NaN.
    const optional: Record<string, number | null> = {};
    for (const spec of NUTRIENTS) {
      const edit = figureChange(detail[spec.per100Key], null, byKey[spec.per100Key]);
      if (edit.state === "invalid") {
        return setProblem(`${spec.label} has to be a number, or left blank.`);
      }
      optional[spec.per100Key] = figureValue(edit);
    }

    const grams = parseDecimal(portion);
    try {
      const food = await create.mutateAsync({
        name: name.trim(),
        brand: brand.trim() || null,
        basis_unit: unit,
        kcal_per_100: energy,
        ...macros,
        ...optional,
        servings:
          Number.isFinite(grams) && grams > 0
            ? [{ label: "1 portion", grams, is_default: true }]
            : [],
      });
      onCreated(food);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Couldn't save that food.");
    }
  };

  return (
    <form
      className={bare ? "manual stack" : "card stack"}
      style={{ gap: "var(--sp-3)" }}
      onSubmit={submit}
    >
      {!bare && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 className="card__title" style={{ margin: 0 }}>
            New food
          </h2>
          <button type="button" className="signin__secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      )}

      <input
        ref={nameRef}
        className="field"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        aria-label="Name"
        autoFocus
      />
      <input
        className="field"
        value={brand}
        onChange={(e) => setBrand(e.target.value)}
        placeholder="Brand (optional)"
        aria-label="Brand"
      />

      <div style={{ display: "flex", gap: "var(--sp-2)", alignItems: "center" }}>
        <span className="muted" style={{ fontSize: "var(--step--1)" }}>
          Nutrition per 100
        </span>
        {(["g", "ml"] as const).map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => setUnit(u)}
            className="num"
            aria-pressed={unit === u}
            style={{
              border: "1px solid var(--hairline)",
              background: unit === u ? "var(--ink)" : "transparent",
              color: unit === u ? "#fff" : "var(--ink-62)",
              borderRadius: "var(--r-pill)",
              padding: "2px 12px",
              fontSize: "var(--step--1)",
            }}
          >
            {u}
          </button>
        ))}
      </div>

      {(
        [
          ["Calories", kcal, setKcal, `Calories per 100 ${unit}`],
          ["Protein", protein, setProtein, `Protein grams per 100 ${unit}`],
          ["Carbs", carbs, setCarbs, `Carbs grams per 100 ${unit}`],
          ["Fat", fat, setFat, `Fat grams per 100 ${unit}`],
        ] as const
      ).map(([label, value, set, described]) => {
        // Blank *here*, in salvage mode, means the panel had this row and the
        // reader missed it. That is a question to answer, not a field nobody
        // has got to — so it says so, and it is the field the eye is sent to.
        const unread = salvage && value === "";
        return (
          <label className="manual__row" key={label}>
            <span>
              {label}
              {unread && <span className="manual__unread"> couldn&rsquo;t read</span>}
            </span>
            <input
              className={`field num manual__value${unread ? " manual__value--unread" : ""}`}
              inputMode="decimal"
              value={value}
              onChange={(e) => set(e.target.value)}
              placeholder={unread ? "—" : "0"}
              aria-label={described}
              required={label === "Calories"}
            />
          </label>
        );
      })}

      {/* What is on the packet under the macros. Behind a toggle because most
          foods are entered from the four figures alone, and four more boxes on
          the way to Save turns a quick form into a chore. */}
      <OptionalDetail defaultOpen={detailRead}>
        {NUTRIENTS.map((spec) => {
          const value = detail[spec.per100Key] ?? "";
          const unread = salvage && value === "";
          return (
            <label className="manual__row" key={spec.key}>
              <span>
                {spec.label}
                {unread && <span className="manual__unread"> couldn&rsquo;t read</span>}
              </span>
              <input
                className={`field num manual__value${unread ? " manual__value--unread" : ""}`}
                inputMode="decimal"
                value={value}
                onChange={(e) =>
                  setDetail((current) => ({ ...current, [spec.per100Key]: e.target.value }))
                }
                /* An em dash, never a 0. Everything in this section is
                   optional, so a placeholder zero would read as the answer
                   rather than as the absence of one. */
                placeholder="—"
                aria-label={`${spec.label} ${spec.unit} per 100 ${unit}`}
              />
            </label>
          );
        })}
      </OptionalDetail>

      <label className="manual__row">
        <span>
          Usual portion
          <span className="muted"> (optional)</span>
        </span>
        <input
          className="field num manual__value"
          inputMode="decimal"
          value={portion}
          onChange={(e) => setPortion(e.target.value)}
          placeholder={unit}
          aria-label={`Usual portion in ${unit}`}
        />
      </label>

      {/* Each form says what its own blanks mean, because the answer differs.
          Finishing a half-read panel, a blank is a row nobody read and must stay
          one. Starting from nothing, the trap is the other way round: the number
          on the packet is usually per serving, and typing it into a per-100
          field makes a food that is wrong for as long as it is logged. */}
      <p className="manual__note">
        {salvage
          ? "A blank stays blank — a zero would assert something nobody read."
          : "Per 100 g, not per serving — a portion figure typed into a per-100 field makes a food that is wrong for as long as you keep logging it."}
      </p>

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}

      <button className="button" type="submit" disabled={create.isPending}>
        {create.isPending ? "Saving…" : "Save and add"}
      </button>
    </form>
  );
}
